import { useEffect } from 'react';

type PageMeta = {
  title: string;
  description: string;
  image?: string;
  type?: string;
};

const siteUrl = 'https://www.silentlabs.ai';

const ensureMetaTag = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => {
      element?.setAttribute(key, value);
    });
    document.head.appendChild(element);
  }

  return element;
};

export const usePageMeta = ({ title, description, image, type = 'website' }: PageMeta) => {
  useEffect(() => {
    const previousTitle = document.title;
    const resolvedImage = image?.startsWith('/') ? `${siteUrl}${image}` : image;
    const currentUrl = window.location.href;
    const trackedMeta = [
      ensureMetaTag('meta[name="description"]', { name: 'description' }),
      ensureMetaTag('meta[property="og:title"]', { property: 'og:title' }),
      ensureMetaTag('meta[property="og:description"]', { property: 'og:description' }),
      ensureMetaTag('meta[property="og:type"]', { property: 'og:type' }),
      ensureMetaTag('meta[property="og:url"]', { property: 'og:url' }),
      ensureMetaTag('meta[property="og:image"]', { property: 'og:image' }),
      ensureMetaTag('meta[name="twitter:title"]', { name: 'twitter:title' }),
      ensureMetaTag('meta[name="twitter:description"]', { name: 'twitter:description' }),
      ensureMetaTag('meta[name="twitter:url"]', { name: 'twitter:url' }),
      ensureMetaTag('meta[name="twitter:image"]', { name: 'twitter:image' }),
      ensureMetaTag('meta[name="twitter:card"]', { name: 'twitter:card' }),
    ];

    const previousValues = trackedMeta.map((meta) => meta?.getAttribute('content') ?? null);

    document.title = title;

    trackedMeta[0]?.setAttribute('content', description);
    trackedMeta[1]?.setAttribute('content', title);
    trackedMeta[2]?.setAttribute('content', description);
    trackedMeta[3]?.setAttribute('content', type);
    trackedMeta[4]?.setAttribute('content', currentUrl);
    trackedMeta[5]?.setAttribute('content', resolvedImage ?? '');
    trackedMeta[6]?.setAttribute('content', title);
    trackedMeta[7]?.setAttribute('content', description);
    trackedMeta[8]?.setAttribute('content', currentUrl);
    trackedMeta[9]?.setAttribute('content', resolvedImage ?? '');
    trackedMeta[10]?.setAttribute('content', 'summary_large_image');

    return () => {
      document.title = previousTitle;

      trackedMeta.forEach((meta, index) => {
        const previousValue = previousValues[index];

        if (!meta) {
          return;
        }

        if (previousValue === null) {
          meta.removeAttribute('content');
          return;
        }

        meta.setAttribute('content', previousValue);
      });
    };
  }, [description, image, title, type]);
};
